import React from 'react';

import {
  ArrowForward,
  Favorite,
  Image,
  ChatBubble,
  WatchLater,
} from '@tmap/mmm-style-guide/src/Icon';
import CircleIcon from '@tmap/mmm-style-guide/src/CircleIcon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Button } from '@tmap/mmm-style-guide/src/Button';

const hideProp = (theme, [direction, breakpoint] = [null, null]) => (
  theme && direction && breakpoint ? {
    [theme.breakpoints[direction](breakpoint)]: {
      display: 'none',
    },
  } : {}
);

const HideGrid = styled(Grid, {
  shouldForwardProp: (p) => p !== 'hide',
})(({ theme, hide }) => ({
  ...hideProp(theme, hide),
}));

const QuickApplyGrid = styled(Grid, {
  shouldForwardProp: (p) => p !== 'hide',
})(({ theme, hide }) => ({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  ...hideProp(theme, hide),
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    marginLeft: theme.spacing(0.5),
  },
}));

const IconContainer = styled(Grid, {
  shouldForwardProp: (p) => p !== 'hide',
})(({ theme, hide }) => ({
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  ...hideProp(theme, hide),
}));

const CountButton = styled(Button)(() => ({
  '& .MuiButton-root': {
    padding: '4px',
    display: 'flex',
    flexFlow: 'column nowrap',
    margin: '0 auto',
  },
  width: '76px',
}));

const ColoredCircle = styled(CircleIcon, {
  shouldForwardProp: (p) => p !== 'color',
})(({ theme, color }) => ({
  color: theme.palette[color].main,
  backgroundColor: theme.palette[color].veryLight,
}));

export default function CommunityCounts(props) {
  const {
    community,
    variant = 'responsive', // responsive, icons, stacked
    notation = 'compact',
    showApplyLink,
    handleQuickApply,
    applyButtonProps = {},
    applyArrowColor = 'secondary',
    noApplyIcon = false,
    responsiveBreakpoint = 'md',
    stackedProps = {},
    iconsProps = {},
  } = props;
  const likeCount = new Intl.NumberFormat(undefined, { notation })
    .format(community.numberOfLikes || 0);
  const postCount = new Intl.NumberFormat(undefined, { notation })
    .format(community.numberOfPosts || 0);
  const mediaCount = new Intl.NumberFormat(undefined, { notation })
    .format(community.numberOfMedia || 0);

  const hideContained = {
    responsive: noApplyIcon
      ? undefined
      : ['down', responsiveBreakpoint],
    icons: noApplyIcon
      ? ['down', responsiveBreakpoint]
      : ['up', 'xs'],
  }[variant];
  const hideStack = {
    responsive: ['down', responsiveBreakpoint],
    icons: ['up', 'xs'],
  }[variant];
  const hideIcons = {
    responsive: ['up', responsiveBreakpoint],
    stacked: ['up', 'xs'],
  }[variant];

  const CircleIcons = [
    (
      <IconContainer item key='icon-container-favorite'>
        <CountButton
          variant='text'
          role='none'
          color='favorite'
          disabled
        >
          <ColoredCircle
            Icon={Favorite}
            variant='contained'
            fontSize='medium'
            color='favorite'
            shrinkFactor='1 / 2'
          />
          <Typography
            variant='caption'
            color='textSecondary'
            marginTop={0.5}
          >
            {likeCount} Likes
          </Typography>
        </CountButton>
      </IconContainer>
    ), (
      <IconContainer item key='icon-container-image'>
        <CountButton
          variant='text'
          role='none'
          color='purple'
          disabled
        >
          <ColoredCircle
            Icon={Image}
            variant='contained'
            fontSize='medium'
            color='purple'
            shrinkFactor='1 / 2'
          />
          <Typography
            variant='caption'
            color='textSecondary'
            marginTop={0.5}
          >
            {mediaCount} Media
          </Typography>
        </CountButton>
      </IconContainer>
    ), (
      <IconContainer item key='icon-container-post'>
        <CountButton
          variant='text'
          role='none'
          color='info'
          disabled
        >
          <ColoredCircle
            Icon={ChatBubble}
            variant='contained'
            fontSize='medium'
            color='info'
            shrinkFactor='1 / 2'
          />
          <Typography
            variant='caption'
            color='textSecondary'
            marginTop={0.5}
          >
            {postCount} Posts
          </Typography>
        </CountButton>
      </IconContainer>
    ), (
      <IconContainer item key='icon-container-apply'>
        <CountButton
          variant='text'
          role='link'
          aria-label='quick apply'
          onClick={handleQuickApply}
          color='success'
        >
          <ColoredCircle
            Icon={WatchLater}
            variant='contained'
            fontSize='medium'
            color='success'
            shrinkFactor='1 / 2'
          />
          <Typography
            variant='caption'
            color='textSecondary'
            marginTop={0.5}
          >
            QuickApply
          </Typography>
        </CountButton>
      </IconContainer>
    ),
  ];

  return (
    <>
      {showApplyLink && (
        <QuickApplyGrid item hide={hideContained}>
          <ApplyButton variant='text' color='inverted' onClick={handleQuickApply} role='link' aria-label='quick apply' {...applyButtonProps}>
            Apply in 5 Minutes <ArrowForward color={applyArrowColor} fontSize='small' />
          </ApplyButton>
        </QuickApplyGrid>
      )}
      <IconContainer item hide={hideStack} {...stackedProps}>
        <ColoredCircle
          Icon={Favorite}
          variant='contained'
          fontSize='medium'
          color='favorite'
          shrinkFactor='1 / 2'
        />
        <Typography variant='body2' component='span' color='textSecondary' textAlign='left' marginLeft={1}>
          {likeCount} Likes
        </Typography>
      </IconContainer>
      <IconContainer item hide={hideStack} {...stackedProps}>
        <ColoredCircle
          Icon={Image}
          variant='contained'
          fontSize='medium'
          color='purple'
          shrinkFactor='1 / 2'
        />
        <Typography variant='body2' component='span' color='textSecondary' textAlign='left' marginLeft={1}>
          {mediaCount} Images and Videos
        </Typography>
      </IconContainer>
      <IconContainer item hide={hideStack} {...stackedProps}>
        <ColoredCircle
          Icon={ChatBubble}
          variant='contained'
          fontSize='medium'
          color='info'
          shrinkFactor='1 / 2'
        />
        <Typography variant='body2' component='span' color='textSecondary' textAlign='left' marginLeft={1}>
          {postCount} Community Posts
        </Typography>
      </IconContainer>
      <HideGrid item xs hide={hideIcons} {...iconsProps}>
        <Grid container justifyContent='space-around'>
          <Grid item xs>
            <Grid container justifyContent='space-around'>
              {showApplyLink && !noApplyIcon ? CircleIcons.slice(0, 2) : CircleIcons.slice(0, 3)}
            </Grid>
          </Grid>
          {showApplyLink && !noApplyIcon && (
            <Grid item xs>
              <Grid container justifyContent='space-around'>
                {CircleIcons.slice(2, 4)}
              </Grid>
            </Grid>
          )}
        </Grid>
      </HideGrid>
    </>
  );
}
